import { useState } from "react";
import { ChevronDown, Send, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import {
  updateTicket,
  type Ticket,
  type TicketCategory,
  type TicketStatus,
} from "@/lib/api";
import { relative, absolute } from "@/lib/format";
import { cn } from "@/lib/cn";

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
};

const STATUS_STYLE: Record<TicketStatus, string> = {
  open: "border-coral bg-coral-soft text-coral-ink",
  in_progress: "border-warning/40 bg-warning-soft text-warning",
  resolved: "border-success/40 bg-success-soft text-success",
  closed: "border-line bg-surface-2 text-text-soft",
};

const CATEGORY_LABEL: Record<TicketCategory, string> = {
  reservation: "Reservation",
  complaint: "Complaint",
  question: "Question",
  other: "Other",
};

const STATUS_ORDER: TicketStatus[] = ["open", "in_progress", "resolved", "closed"];

/**
 * One ticket, collapsed to a summary row that expands to the full message,
 * status controls, a reply box (emails the camper), and private notes.
 */
export function TicketRow({
  ticket,
  onUpdated,
}: {
  ticket: Ticket;
  onUpdated: (t: Ticket) => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [notes, setNotes] = useState(ticket.admin_notes ?? "");
  const [busy, setBusy] = useState(false);

  const pageUrl = (ticket.visitor_metadata?.page_url as string | undefined) ?? null;

  const patch = async (input: { status?: TicketStatus; admin_notes?: string; reply?: string }) => {
    setBusy(true);
    try {
      const r = await updateTicket({ ticket_id: ticket.id, ...input });
      onUpdated(r.ticket);
      if (input.reply) {
        setReply("");
        toast.push(
          r.emailed
            ? `Reply sent to ${ticket.visitor_email}.`
            : "Saved, but the reply email didn't go out (email isn't configured).",
          r.emailed ? "success" : "error",
        );
      } else if (input.status) {
        toast.push(`Marked ${STATUS_LABEL[input.status].toLowerCase()}.`);
      } else {
        toast.push("Notes saved.");
      }
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Couldn't update", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card overflow-hidden">
      {/* Summary row */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 px-5 py-4 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={ticket.status} />
            <span className="text-2xs uppercase tracking-wide text-text-faint">
              {CATEGORY_LABEL[ticket.category]}
            </span>
            <span className="text-2xs tabular-nums text-text-faint">{ticket.reference}</span>
          </div>
          <p className="mt-1.5 truncate text-sm font-medium text-text-strong">{ticket.subject}</p>
          <p className="mt-0.5 truncate text-xs text-text-soft">
            {ticket.visitor_name} · {ticket.visitor_email} ·{" "}
            <span title={absolute(ticket.created_at)}>{relative(ticket.created_at)}</span>
          </p>
        </div>
        <ChevronDown
          size={18}
          strokeWidth={1.75}
          className={cn("mt-0.5 shrink-0 text-text-faint transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="space-y-4 border-t border-line px-5 py-4">
          {/* Original message */}
          <div>
            <p className="text-2xs font-medium uppercase tracking-wide text-text-faint">Message</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-text-strong">{ticket.message}</p>
            {pageUrl && (
              <p className="mt-2 text-xs text-text-soft">
                Sent from{" "}
                <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="text-coral hover:underline">
                  {pageUrl}
                </a>
              </p>
            )}
          </div>

          {/* Status controls */}
          <div>
            <p className="text-2xs font-medium uppercase tracking-wide text-text-faint">Status</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={busy || s === ticket.status}
                  onClick={() => void patch({ status: s })}
                  className={cn(
                    "inline-flex items-center rounded-full border px-2.5 py-1 text-xs transition-colors disabled:cursor-default",
                    s === ticket.status
                      ? STATUS_STYLE[s]
                      : "border-line bg-surface text-text-soft hover:bg-surface-2 hover:text-text-strong",
                  )}
                >
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          {/* Reply */}
          <div>
            <label className="text-2xs font-medium uppercase tracking-wide text-text-faint">
              Reply by email
            </label>
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={4}
              placeholder={`Write back to ${ticket.visitor_name}…`}
              disabled={busy}
              className="mt-1.5 block w-full resize-y rounded border border-line bg-surface px-3 py-2 text-sm text-text-strong outline-none focus:border-coral"
            />
            <div className="mt-2 flex justify-end">
              <Button
                variant="primary"
                size="sm"
                disabled={busy || reply.trim().length === 0}
                loading={busy}
                onClick={() => void patch({ reply: reply.trim() })}
                iconLeft={!busy && <Send size={12} strokeWidth={1.75} />}
              >
                Send reply
              </Button>
            </div>
          </div>

          {/* Private notes */}
          <div>
            <label className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-text-faint">
              <StickyNote size={12} strokeWidth={1.75} /> Private notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Only you can see this."
              disabled={busy}
              className="mt-1.5 block w-full resize-y rounded border border-line bg-surface px-3 py-2 text-sm text-text-strong outline-none focus:border-coral"
            />
            {notes.trim() !== (ticket.admin_notes ?? "") && (
              <div className="mt-2 flex justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => void patch({ admin_notes: notes })}
                >
                  Save notes
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: TicketStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-2xs font-medium",
        STATUS_STYLE[status],
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}
